import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { esperarHomeListo, marcarSplashTerminado, precargarImagen } from '../utils/arranque';
import { medirTinta } from '../utils/medirTinta';
import { fijarColorSplash } from '../hooks/useThemeColor';
import { getDefaultBusinessCached, leerRestauranteGuardado } from '../services/businessService';

// Curva bezier estilo Apple/Google para animaciones fluidas
const smoothEase = [0.22, 1, 0.36, 1];

// El splash sale antes de que llegue cualquier dato, así que su configuración
// (Business.splash: color, fondo, logo, parte de abajo) sale de la copia del
// restaurante guardada en el celular. Con eso abre al instante con las
// imágenes del dashboard; cada apertura refresca esa copia para la siguiente.

// Como en la referencia (bistro): pantalla del color liso, el fondo con la
// comida aparece con un fundido corto, el logo entra con su rebote (crece un
// poco desde abajo, como lo tenía antes) y se corta directo al home, sin
// fundido de salida; el home hace su propia entrada (useEntradaHome).
// Tiempo mínimo en pantalla desde que empiezan a aparecer (fundido incluido),
// y tope absoluto: con mala señal no se queda trabado. Entre uno y otro, el
// splash espera a que el home avise que ya está armado.
const DURACION_MS = 1200;
const TOPE_MS = 5000;
const FUNDIDO = { duration: 0.28, ease: 'easeOut' };

// Dónde va el logo, en el lienzo del fondo (medido del diseño de referencia):
// su dibujo ocupa hasta el 73% del ancho y el 16% del alto, centrado a lo
// ancho y con su centro al 21.3% del alto. Se mide la tinta del PNG (sin el
// aire transparente), así da igual si se exporta recortado o con margen.
const LOGO_ANCHO = 0.73;
const LOGO_ALTO = 0.16;
const LOGO_CENTRO_Y = 0.213;

// Solo el home avisa que está listo. Si se abre directo otra pantalla
// (carrito, seguimiento…), el splash no lo espera.
const abreEnHome = () => ['/', '/home'].includes(window.location.pathname);
// Primera vez (sin copia guardada): cuánto esperar a la API antes de usar el
// splash por defecto.
const ESPERA_API_MS = 1200;

const leerCache = () => leerRestauranteGuardado()?.splash || null;

const tieneImagenes = (s) => !!(s && (s.background || s.logo || s.bottom));

// Pide el splash. null si no hay o falla. Usa la misma petición del
// restaurante que el resto de la app (se monta detrás del splash y la pide al
// mismo tiempo): una sola llamada a la API, que además deja la copia guardada.
const pedirSplash = async () => {
  try {
    return (await getDefaultBusinessCached())?.splash || null;
  } catch {
    return null;
  }
};

// Posición del logo en % del lienzo (ancho W, alto H, aspecto = W/H).
function colocarLogo(tinta, aspectoLienzo) {
  // Si no se pudo medir: el PNG a todo el ancho del lienzo, que es como viene
  // exportado el diseño actual.
  if (!tinta) {
    return { width: '100%', left: '0%', top: `${LOGO_CENTRO_Y * 100}%`, transform: 'translateY(-50%)' };
  }
  // Ancho de la tinta en fracción del lienzo: el 73%, salvo que un logo más
  // alto que el actual se pase del 16% del alto.
  const aspectoTinta = (tinta.ancho * tinta.aspecto) / tinta.alto;
  const tintaW = Math.min(LOGO_ANCHO, (LOGO_ALTO / aspectoLienzo) * aspectoTinta);
  const imgW = tintaW / tinta.ancho; // ancho del PNG completo, en fracción de W
  const imgH = (imgW * aspectoLienzo) / tinta.aspecto; // alto del PNG, en fracción de H
  return {
    width: `${imgW * 100}%`,
    left: `${(0.5 - (tinta.x + tinta.ancho / 2) * imgW) * 100}%`,
    top: `${(LOGO_CENTRO_Y - (tinta.y + tinta.alto / 2) * imgH) * 100}%`,
  };
}

/**
 * Pantalla de inicio. Con imágenes del dashboard: el fondo cubre la pantalla
 * como object-cover y el logo va encima en las coordenadas del MISMO lienzo,
 * así queda igual que en el diseño en cualquier celular (aunque el fondo se
 * recorte por los lados o por arriba). La parte de abajo (opcional) va a todo
 * el ancho pegada al pie. Sin imágenes, el splash por defecto (logo + slogan).
 */
const SplashScreen = ({ onFinish }) => {
  // undefined = todavía decidiendo; null = splash por defecto.
  const [config, setConfig] = useState(() => (tieneImagenes(leerCache()) ? leerCache() : undefined));
  // { aspecto del fondo, tinta del logo } cuando todo está descargado.
  const [listo, setListo] = useState(null);
  const terminado = useRef(false);

  const terminar = () => {
    if (terminado.current) return;
    terminado.current = true;
    // El color de fondo puesto desde index.html (para que antes de cargar la
    // app ya se viera el del splash) ya no hace falta: en el resto de la app
    // asomaría al estirar la pantalla.
    document.documentElement.style.background = '';
    fijarColorSplash(null);
    marcarSplashTerminado();
    onFinish();
  };

  // Barra de estado del color del splash mientras está encima.
  const color = config?.color || leerCache()?.color;
  useEffect(() => {
    if (color) fijarColorSplash(color);
  }, [color]);

  // Resolver la configuración: la de caché ya está; si no hay, esperar un poco
  // a la API. Siempre se refresca la caché para la próxima apertura.
  useEffect(() => {
    const peticion = pedirSplash();
    if (config !== undefined) return;
    let vivo = true;
    const espera = setTimeout(() => { if (vivo) setConfig(null); }, ESPERA_API_MS);
    peticion.then((s) => {
      if (!vivo) return;
      clearTimeout(espera);
      setConfig((actual) => (actual === undefined ? (tieneImagenes(s) ? s : null) : actual));
    });
    return () => { vivo = false; clearTimeout(espera); };
    // Solo al montar: la caché se lee una vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Precargar las imágenes (y medir el logo) antes de animar, para que no
  // aparezcan a medias ni brinquen al acomodarse.
  useEffect(() => {
    if (config === undefined) return;
    let vivo = true;
    const s = config || {};
    Promise.all([
      precargarImagen(s.background),
      precargarImagen(s.logo),
      precargarImagen(s.bottom),
      medirTinta(s.logo),
    ]).then(([fondo, , , tinta]) => {
      if (!vivo) return;
      const aspecto = fondo?.naturalWidth
        ? fondo.naturalWidth / fondo.naturalHeight
        : window.innerWidth / window.innerHeight;
      setListo({ aspecto, tinta });
    });
    return () => { vivo = false; };
  }, [config]);

  // Se va cuando terminó su animación Y el home ya está armado detrás.
  useEffect(() => {
    if (!listo) return;
    let vivo = true;
    const animacion = new Promise((r) => setTimeout(r, DURACION_MS));
    Promise.all([animacion, abreEnHome() ? esperarHomeListo() : null])
      .then(() => { if (vivo) terminar(); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listo]);

  useEffect(() => {
    const t = setTimeout(terminar, TOPE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const conImagenes = !!listo && tieneImagenes(config);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      // Corte directo al home, como en la referencia.
      exit={{ opacity: 0, transition: { duration: 0 } }}
      // Por encima de todo: la app ya está montada detrás y algunos avisos
      // (instalar la PWA) usan z-[100]; ninguno debe asomar sobre el splash.
      // Color liso del splash (el mismo que se pinta antes de que cargue la
      // app, desde index.html) mientras se descargan las imágenes.
      className="fixed inset-0 z-[300] bg-splash-bg overflow-hidden"
      style={color ? { backgroundColor: color } : undefined}
    >
      {conImagenes && (
        <>
          {/* Lienzo del diseño: el aspecto del fondo, cubriendo la pantalla
              (lo mismo que object-cover) y centrado. El logo se posiciona en
              % de este lienzo, no de la pantalla. */}
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: `max(100%, calc(100dvh * ${listo.aspecto}))`, aspectRatio: listo.aspecto }}
          >
            {config.background && (
              <motion.img
                src={config.background}
                alt=""
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ ...FUNDIDO, delay: 0.08 }}
                className="absolute inset-0 w-full h-full"
              />
            )}
            {config.logo && (
              // La caja fija la posición del diseño; la imagen de adentro hace
              // la animación (si el transform fuera en la caja, se pelearía
              // con el centrado).
              <div className="absolute" style={colocarLogo(listo.tinta, listo.aspecto)}>
                <motion.img
                  src={config.logo}
                  alt="VaChido"
                  initial={{ opacity: 0, scale: 0.8, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 170, damping: 18, delay: 0.1 }}
                  className="block w-full h-auto"
                />
              </div>
            )}
          </div>

          {/* Parte de abajo (opcional): a todo el ancho y pegada al pie de la
              pantalla, con su alto natural. */}
          {config.bottom && (
            <motion.img
              src={config.bottom}
              alt=""
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ ...FUNDIDO, delay: 0.12 }}
              className="absolute inset-x-0 bottom-0 w-full h-auto"
            />
          )}
        </>
      )}

      {/* Splash por defecto: sin imágenes en el dashboard (o mientras se decide). */}
      {listo && !conImagenes && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.img
            src="/icons/vachido.png"
            alt="VaChido"
            initial={{ scale: 1.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1, y: -15 }}
            transition={{
              scale: { type: 'spring', stiffness: 150, damping: 20, delay: 0.1 },
              opacity: { duration: 0.25, ease: smoothEase },
              y: { type: 'spring', stiffness: 150, damping: 20, delay: 0.1 },
            }}
            className="w-64 h-64 object-contain"
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: smoothEase, delay: 0.4 }}
            className="text-white/80 text-sm -mt-16"
          >
            Con los mejores precios
          </motion.p>
        </div>
      )}
    </motion.div>
  );
};

export default SplashScreen;
